import { ContextSwitcher } from '@/components/shell/context-switcher';
import { CommandPalette } from '@/components/command-palette';
import { RequireAuth } from '@/components/shell/require-auth';
import { UserMenu } from '@/components/shell/user-menu';
import { cn } from '@/lib/cn';
import { globalCommandContext } from '@/lib/editor-commands';
import { Link, Outlet, createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  BarChart3,
  Boxes,
  Home,
  Image,
  Layers3,
  Menu,
  Palette,
  Rocket,
  Route as RouteIcon,
  Settings,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/_authed')({ component: Shell });

type NavItem = {
  to: '/home' | '/install' | '/experiences' | '/journeys' | '/campaigns' | '/segments' | '/analytics' | '/assets' | '/theme' | '/settings';
  label: string;
  icon: LucideIcon;
  planned?: boolean;
};

const GROUPS: Array<{ label?: string; items: NavItem[] }> = [
  {
    items: [
      { to: '/home', label: 'Home', icon: Home },
      { to: '/install', label: 'Install', icon: Rocket },
    ],
  },
  {
    label: 'Build',
    items: [
      { to: '/experiences', label: 'Experiences', icon: Layers3 },
      { to: '/journeys', label: 'Journeys', icon: RouteIcon, planned: true },
    ],
  },
  {
    label: 'Engage',
    items: [
      { to: '/campaigns', label: 'Campaigns', icon: Boxes },
      { to: '/segments', label: 'Audiences', icon: Users },
    ],
  },
  {
    label: 'Measure',
    items: [{ to: '/analytics', label: 'Analytics', icon: BarChart3, planned: true }],
  },
  {
    label: 'Library',
    items: [
      { to: '/assets', label: 'Assets', icon: Image },
      { to: '/theme', label: 'Theme', icon: Palette },
    ],
  },
];

const PAGE_NAMES = new Map(GROUPS.flatMap((group) => group.items.map((item) => [item.to, item.label])));

function SidebarNav({ pathname, onNavigate }: { pathname: string; onNavigate?: () => void }): React.JSX.Element {
  return (
    <>
      <Link to="/home" onClick={onNavigate} className="flex h-14 items-center gap-2 px-3">
        <span className="grid size-7 place-items-center rounded-md bg-foreground text-xs font-bold text-background">P</span>
        <span className="text-sm font-semibold">PersonalizeHQ</span>
      </Link>

      <div className="px-2 pb-3">
        <ContextSwitcher {...(onNavigate ? { onNavigate } : {})} />
      </div>

      <nav aria-label="Main navigation" className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
        {GROUPS.map((group, index) => (
          <div key={group.label ?? 'home'} className={cn(index > 0 && 'mt-5')}>
            {group.label ? (
              <p className="mb-1 px-2 text-[10px] font-semibold uppercase text-muted-foreground">{group.label}</p>
            ) : null}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const active = pathname === item.to || (item.to !== '/home' && pathname.startsWith(`${item.to}/`));
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    onClick={onNavigate}
                    className={cn(
                      'group flex h-9 items-center gap-2.5 rounded-md px-2 text-[13px] transition-colors',
                      active
                        ? 'bg-background font-medium text-foreground shadow-sm ring-1 ring-border'
                        : 'text-muted-foreground hover:bg-background/70 hover:text-foreground'
                    )}
                  >
                    <Icon className={cn('size-4', active ? 'text-indigo-600 dark:text-indigo-400' : 'text-muted-foreground')} />
                    <span>{item.label}</span>
                    {item.planned ? (
                      <span className="ml-auto rounded border px-1 py-0.5 text-[8px] font-semibold uppercase text-muted-foreground">
                        Soon
                      </span>
                    ) : null}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t p-2">
        <Link
          to="/settings"
          onClick={onNavigate}
          className={cn(
            'flex h-9 items-center gap-2.5 rounded-md px-2 text-[13px] transition-colors',
            pathname.startsWith('/settings')
              ? 'bg-background font-medium shadow-sm ring-1 ring-border'
              : 'text-muted-foreground hover:bg-background/70 hover:text-foreground'
          )}
        >
          <Settings className="size-4" />
          Settings
        </Link>
      </div>
    </>
  );
}

function Shell(): React.ReactNode {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const activeRoot = [...PAGE_NAMES.keys()].find((path) => pathname === path || pathname.startsWith(`${path}/`));
  const pageName = activeRoot ? PAGE_NAMES.get(activeRoot) : 'Workspace';

  return (
    <RequireAuth>
      <div className="flex h-screen overflow-hidden bg-background text-[13px]">
        <aside className="hidden w-[236px] shrink-0 flex-col border-r bg-zinc-50/90 md:flex dark:bg-zinc-950">
          <SidebarNav pathname={pathname} />
        </aside>

        {mobileOpen ? (
          <div className="fixed inset-0 z-50 md:hidden">
            <button
              type="button"
              aria-label="Close navigation"
              className="absolute inset-0 bg-black/30"
              onClick={() => setMobileOpen(false)}
            />
            <aside className="relative flex h-full w-[280px] flex-col border-r bg-zinc-50 shadow-xl dark:bg-zinc-950">
              <button
                type="button"
                aria-label="Close navigation"
                className="absolute right-3 top-3 grid size-8 place-items-center rounded-md hover:bg-secondary"
                onClick={() => setMobileOpen(false)}
              >
                <X className="size-4" />
              </button>
              <SidebarNav pathname={pathname} onNavigate={() => setMobileOpen(false)} />
            </aside>
          </div>
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 shrink-0 items-center border-b px-4 sm:px-6">
            <button
              type="button"
              aria-label="Open navigation"
              className="mr-3 grid size-8 place-items-center rounded-md hover:bg-secondary md:hidden"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="size-4" />
            </button>
            <p className="text-sm font-medium">{pageName}</p>
            <div className="ml-auto">
              <div className="flex items-center gap-2">
                <CommandPalette context={globalCommandContext(pathname, (path) => void navigate({ to: path }))} />
                <UserMenu />
              </div>
            </div>
          </header>

          <main className="min-w-0 flex-1 overflow-y-auto">
            <div className="mx-auto w-full max-w-[1240px] px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </RequireAuth>
  );
}
